/**
 * Chrome FAB for setup-tutorial steps + cross-feature nudges. Classified
 * as a `shell` element (not `shared`) so it can read members/bookings/
 * dashboard tutorial config — see the `shell` allow-list in eslint.config.js.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Check, ChevronDown, X } from 'lucide-react'
import { useNotificationCenterStore } from './notification-center-store'
import { useAuthStore } from '@/core/auth/auth-store'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { useCurrentRole } from '@/features/staff/hooks/use-current-role'
import { crmLive, posLive } from '@/core/models/club'
import { useClub } from '@/features/settings/hooks/use-club'
import { useStaff } from '@/features/staff/hooks/use-staff'
import {
  OWNER_TUTORIAL,
  type OwnerTutorialCtx,
} from '@/features/dashboard/components/owner-tutorial'
import {
  MANAGER_SETUP_TUTORIAL,
  type ManagerSetupCtx,
  isManagerReservasSeen,
  isManagerActividadSeen,
  isManagerHorarioSeen,
} from '@/features/dashboard/components/manager-setup-tutorial'
import {
  RECEPTION_SETUP_TUTORIAL,
  type ReceptionSetupCtx,
  isReceptionHoySeen,
  isReceptionCobrarSeen,
  isReceptionClientesSeen,
  isReceptionVentasSeen,
} from '@/features/dashboard/components/reception-setup-tutorial'
import { isPlatformWelcomeSeen } from '@/features/dashboard/components/platform-welcome-step'
import type { Tutorial } from './tutorial/types'

/**
 * "Centro de novedades" — chrome surface for onboarding tutorials and
 * (in future iterations) news / tips. The standalone bottom-right FAB
 * was folded into the bottom `ActionDock` (spec/clubmanager/15 BR-17): this owns
 * the **panel** and computes the pending count, but the *trigger* lives
 * in the dock. Open-state + pending flow through
 * `useNotificationCenterStore`; app-shell injects the count + opener
 * into the dock (badge on the Novedades icon, dot on the collapsed
 * peek-handle). Nothing pending ⇒ the dock shows no Novedades affordance
 * at all, so established clubs / seasoned receptionists see nothing.
 *
 * V1 hosts only the role-aware setup tutorial:
 *   tier === 'owner'   → OWNER_TUTORIAL          (6 steps, operational)
 *   tier === 'manager' → MANAGER_SETUP_TUTORIAL  (4 steps: reservas, actividad, horario, install)
 *   tier === 'staff'   → RECEPTION_SETUP_TUTORIAL (4 steps: hoy, cobrar, clientes, ventas)
 *
 * Every owner sees the same operational tutorial regardless of how the
 * club came to be. The steps use owner review stamps, so starter values
 * seeded by either onboarding path do not mark the review as complete.
 *
 * Future iterations will add news (release notes), tips, and any other
 * passive notifications under the same panel — see welcome-by-role.md.
 */

type ActiveTutorial =
  | { kind: 'owner'; tutorial: Tutorial<OwnerTutorialCtx>; ctx: OwnerTutorialCtx }
  | { kind: 'manager'; tutorial: Tutorial<ManagerSetupCtx>; ctx: ManagerSetupCtx }
  | { kind: 'reception'; tutorial: Tutorial<ReceptionSetupCtx>; ctx: ReceptionSetupCtx }

/**
 * Running as an installed PWA? Two-line runtime probe (display-mode
 * standalone + iOS legacy `navigator.standalone`). The canonical *pure*
 * definition is `isStandalone()` in
 * features/install/domain/install-detection.ts; this shell picker reads the
 * runtime directly rather than cross-import a feature for two lines.
 */
function readStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const mm = window.matchMedia?.('(display-mode: standalone)').matches === true
  const ios =
    (navigator as unknown as { standalone?: boolean }).standalone === true
  return mm || ios
}

interface PickerInputs {
  tier: 'owner' | 'manager' | 'staff' | null
  club: {
    status: string
    pricesReviewedAt: Date | null | undefined
    hoursReviewedAt: Date | null | undefined
    courtsReviewedAt: Date | null | undefined
    catalogReviewedAt: Date | null | undefined
    analyticsViewedAt: Date | null | undefined
  } | null
  staffCount: number
  /** Manager opened Reservas / Actividad / Horario from the tour once —
   * orientation acks (localStorage-backed; managers can't write the club doc). */
  managerReservasSeen: boolean
  managerActividadSeen: boolean
  managerHorarioSeen: boolean
  /** Receptionist opened Hoy / Cobrar / Clientes / Ventas from the tour once —
   * orientation acks (localStorage-backed; staff can't write the club doc). */
  receptionHoySeen: boolean
  receptionCobrarSeen: boolean
  receptionClientesSeen: boolean
  receptionVentasSeen: boolean
  /** Installed (standalone) OR install instructions seen once — shared
   * platform-welcome step completion, injected into the manager ctx (the
   * owner + reception tours no longer carry an install step). */
  platformWelcomeDone: boolean
}

function pickActiveTutorial(inputs: PickerInputs): ActiveTutorial | null {
  const { tier, club } = inputs
  if (!club || club.status === 'mockup' || tier == null) return null
  if (tier === 'owner') {
    return {
      kind: 'owner',
      tutorial: OWNER_TUTORIAL,
      ctx: {
        courtsReviewed: club.courtsReviewedAt != null,
        hoursReviewed: club.hoursReviewedAt != null,
        pricesReviewed: club.pricesReviewedAt != null,
        catalogReviewed: club.catalogReviewedAt != null,
        analyticsReviewed: club.analyticsViewedAt != null,
        staffCount: inputs.staffCount,
      },
    }
  }
  if (tier === 'manager') {
    return {
      kind: 'manager',
      tutorial: MANAGER_SETUP_TUTORIAL,
      ctx: {
        reservasSeen: inputs.managerReservasSeen,
        actividadSeen: inputs.managerActividadSeen,
        horarioSeen: inputs.managerHorarioSeen,
        platformWelcomeDone: inputs.platformWelcomeDone,
      },
    }
  }
  return {
    kind: 'reception',
    tutorial: RECEPTION_SETUP_TUTORIAL,
    ctx: {
      hoySeen: inputs.receptionHoySeen,
      cobrarSeen: inputs.receptionCobrarSeen,
      clientesSeen: inputs.receptionClientesSeen,
      ventasSeen: inputs.receptionVentasSeen,
    },
  }
}

export function NotificationCenter() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const clubId = useCurrentClubStore((s) => s.clubId)
  const role = useCurrentRole()
  const clubQuery = useClub(clubId)
  const staffQuery = useStaff(clubId)

  const open = useNotificationCenterStore((s) => s.open)
  // Re-read optional orientation storage on render, including reopening the panel.
  const active: ActiveTutorial | null = (() => {
    const c = clubQuery.data
    // Drive the tutorial from the REAL staff role, not the
    // permission-passthrough role. Platform admins and preview-token
    // visitors both get a synthetic `owner` role for permission gates
    // (so they can see every screen) but they're not actual staff in
    // this club — showing them the owner first-steps wizard is wrong.
    // No staff doc → no tutorial.
    const realTier = role.staffRole == null
      ? null
      : role.tier  // staffRole present ⇒ role === staffRole ⇒ tier derived from real role
    const picked = pickActiveTutorial({
      tier: realTier,
      club: c
        ? {
            status: c.status,
            pricesReviewedAt: c.pricesReviewedAt,
            hoursReviewedAt: c.hoursReviewedAt,
            courtsReviewedAt: c.courtsReviewedAt,
            catalogReviewedAt: c.catalogReviewedAt,
            analyticsViewedAt: c.analyticsViewedAt,
          }
        : null,
      staffCount: staffQuery.data?.length ?? 0,
      managerReservasSeen: isManagerReservasSeen(),
      managerActividadSeen: isManagerActividadSeen(),
      managerHorarioSeen: isManagerHorarioSeen(),
      receptionHoySeen: isReceptionHoySeen(),
      receptionCobrarSeen: isReceptionCobrarSeen(),
      receptionClientesSeen: isReceptionClientesSeen(),
      receptionVentasSeen: isReceptionVentasSeen(),
      platformWelcomeDone: readStandalone() || isPlatformWelcomeSeen(),
    })
    if (!picked || picked.kind === 'owner') return picked
    const steps = picked.tutorial.steps.filter(step => {
      if (picked.kind === 'reception') {
        if (step.id === 'hoy') return role.can('viewDashboard')
        if (step.id === 'cobrar') return role.can('recordPayment')
        if (step.id === 'clientes') return crmLive(c) && role.can('viewCrm')
        if (step.id === 'ventas') return posLive(c) && role.can('createSale')
      }
      if (step.id === 'actividad') return role.can('viewActivityLog')
      if (step.id === 'horario') return role.can('editClubProfile')
      return true
    }).map(step => step.id === 'hoy' && !role.can('createBooking')
      ? { ...step, hint: 'Consulta las cifras del día, el mapa de pistas y lo pendiente.' }
      : step)
    return { ...picked, tutorial: { ...picked.tutorial, steps, intro: 'Abre las secciones de tu guía para familiarizarte con tu trabajo en este club.' } } as ActiveTutorial
  })()

  const closeCenter = useNotificationCenterStore((s) => s.closeCenter)
  const setPending = useNotificationCenterStore((s) => s.setPending)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  // Close on Escape; close on outside click. Both only register while
  // the panel is open so we don't pay the listener cost on every page.
  // The trigger now lives in the dock, so a click inside `.action-dock`
  // must NOT count as "outside" (it would close the panel the same tick
  // it was opened).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCenter()
    }
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (panelRef.current?.contains(target)) return
      if (
        target instanceof Element &&
        target.closest('.action-dock') != null
      )
        return
      closeCenter()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open, closeCenter])

  // Compute pending count up front so we can render a single conditional
  // tree below — early returns without hooks would violate Rules of Hooks
  // if the hook count differs between renders.
  const computed = useMemo(() => {
    if (!active) return null
    const stepStates = active.tutorial.steps.map((s) => ({
      step: s,
      done: s.isComplete(active.ctx as never),
    }))
    const completed = stepStates.filter((s) => s.done).length
    return {
      stepStates,
      completed,
      total: active.tutorial.steps.length,
      pending: active.tutorial.steps.length - completed,
    }
  }, [active])

  // Publish the pending count for the dock badge/handle-dot. Runs every
  // render (incl. 0 / not-signed-in) so the dock affordance clears the
  // moment there's nothing left — must be before any early return.
  const pending = !user || !clubId ? 0 : computed?.pending ?? 0
  useEffect(() => {
    setPending(pending)
  }, [pending, setPending])

  // Open the first novedad by default when the panel opens. With a single
  // item the collapsed row is pure friction — the operator just wants to see
  // what's pending. Keyed on the tutorial *id* (stable string), not the
  // `active` object, so a background data refresh doesn't re-open an item the
  // operator just collapsed. Single-open accordion: `expandedId` holds one id,
  // so clicking a different item switches which is open and re-clicking the
  // open one collapses it (see `onToggleExpand`). The body animates via the
  // `.collapsible` grid-rows transition. (Multi-item future — news/tips — would
  // default to `items[0].id`.)
  const activeTutorialId = active?.tutorial.id ?? null
  useEffect(() => {
    setExpandedId(open && activeTutorialId ? activeTutorialId : null)
  }, [open, activeTutorialId])

  if (!user || !clubId) return null
  if (!active || !computed || computed.pending === 0) return null
  if (!open) return null

  const isExpanded = expandedId === active.tutorial.id
  const onToggleExpand = () => {
    setExpandedId((prev) => (prev === active.tutorial.id ? null : active.tutorial.id))
  }

  return (
    <div
      ref={panelRef}
      className="notif-panel"
      role="dialog"
          aria-label="Centro de novedades"
        >
          <header className="notif-panel-head">
            <span className="notif-panel-title">Novedades</span>
            <button
              type="button"
              className="notif-panel-close"
              onClick={closeCenter}
              aria-label="Cerrar"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </header>

          <div className="notif-panel-body">
            <article className={`notif-item ${isExpanded ? 'is-open' : ''}`}>
              <button
                type="button"
                className="notif-item-row"
                aria-expanded={isExpanded}
                onClick={onToggleExpand}
              >
                <span className="notif-item-kind">Configuración</span>
                <span className="notif-item-title">{active.tutorial.title}</span>
                <span className="notif-item-summary mono">
                  {computed.completed} / {computed.total}
                </span>
                <ChevronDown
                  size={14}
                  className="notif-item-chev"
                  aria-hidden="true"
                />
              </button>

              {/* Always-mounted body wrapped in the shared `.collapsible`
                  utility so the row animates open/close with the same
                  timing as ConfigPanel and the help-panel guides. `inert`
                  pulls a closed body out of the focus order. */}
              <div
                className={`collapsible ${isExpanded ? 'collapsible--open' : ''}`.trim()}
                inert={!isExpanded}
                aria-hidden={!isExpanded}
              >
                <div className="collapsible-inner">
                  <div className="notif-item-body">
                    {active.tutorial.intro && (
                      <p className="notif-item-intro">{active.tutorial.intro}</p>
                    )}
                    <ul className="notif-step-list" role="list">
                      {computed.stepStates.map(({ step, done }) => (
                        <li
                          key={step.id}
                          className={`notif-step ${done ? 'is-done' : ''}`}
                        >
                          <span
                            className={`notif-step-mark ${done ? 'is-done' : ''}`}
                            aria-hidden="true"
                          >
                            {done ? <Check size={11} /> : <span className="dot" />}
                          </span>
                          <div className="notif-step-body">
                            <div className="notif-step-label">{step.label}</div>
                            <div className="notif-step-hint">{step.hint}</div>
                          </div>
                          {step.deepLink && !done && (
                            <button
                              type="button"
                              className="notif-step-cta"
                              onClick={() => {
                                step.onActivate?.()
                                closeCenter()
                                // Guarded by `step.deepLink &&` on the parent
                                // ternary — TS narrows the field on this render
                                // but not inside the closure.
                                navigate(step.deepLink!.to)
                              }}
                            >
                              {step.deepLink.label}
                              <ArrowRight size={11} aria-hidden="true" />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
  )
}
